/**
 * How a submitted stop list is turned into routes.
 *
 * Two paths reach the Java routing engine (OPTIMIZE via /pdp-long, PREDEFINED_VRP via
 * /vrp-long) and three are handled by the backend's /routes/orders-fixed-order endpoint
 * without the engine. All five produce the same solution shape, so the chart, the map, the
 * drag-to-adjust flow and the accept path are identical afterwards.
 */
export const STOP_ORDER_MODES = {
  OPTIMIZE: "optimize",
  PREDEFINED_VRP: "predefined_vrp",
  PREDEFINED_OSRM: "predefined_osrm",
  BYPASS: "bypass",
  OSRM_OPTIMIZE: "osrm_optimize"
};

export const DEFAULT_STOP_ORDER_MODE = STOP_ORDER_MODES.OPTIMIZE;

/** Modes served by /routes/orders-fixed-order rather than the routing engine. */
export const FIXED_ORDER_ENDPOINT_MODES = [
  STOP_ORDER_MODES.PREDEFINED_OSRM,
  STOP_ORDER_MODES.BYPASS,
  STOP_ORDER_MODES.OSRM_OPTIMIZE
];

/** Modes that keep the user's sequence instead of choosing one. */
export const ORDER_PRESERVING_MODES = [
  STOP_ORDER_MODES.PREDEFINED_VRP,
  STOP_ORDER_MODES.PREDEFINED_OSRM,
  STOP_ORDER_MODES.BYPASS
];

/**
 * OSRM's trip service rejects requests above 100 coordinates and cannot be chunked without
 * discarding the optimization, so one route can hold at most 99 stops plus the depot.
 */
export const OSRM_TRIP_MAX_STOPS_PER_ROUTE = 99;

/**
 * Presentation order for the picker, which is not the declaration order of STOP_ORDER_MODES:
 * the two modes users should reach for come first, and the two disabled ones sink to the
 * bottom. `info` is the (i) tooltip copy; `disabled` modes stay listed so the tooltip can
 * explain why they are off rather than leaving them unexplained by their absence.
 */
export const STOP_ORDER_MODE_OPTIONS = [
  {
    value: STOP_ORDER_MODES.OPTIMIZE,
    label: "Normal Routing Process",
    info: "Uses the default Flexio Routing Engine"
  },
  {
    value: STOP_ORDER_MODES.PREDEFINED_OSRM,
    label: "Prerouted - keep order",
    info:
      "Keeps stops in the order you provide, and uses OSRM to give estimates. Supports up " +
      'to 100 stops. Uses the optional "Prerouted Route ID" column to separate routes. If ' +
      "you have more than 1 route worth of stops but no Prerouted Route ID, it will be " +
      "split based on default " +
      "number of stops (e.g. 15)"
  },
  {
    value: STOP_ORDER_MODES.OSRM_OPTIMIZE,
    label: "OSRM Routing",
    info:
      "Uses OSRM for routing. Limit 100 stops. Faster than default Flexio Routing Engine " +
      "but less optimized and 100 stop limit"
  },
  {
    value: STOP_ORDER_MODES.BYPASS,
    label: "Bypass Routing",
    disabled: true,
    info:
      "Keeps prerouting / stop order, and uses haversine estimates. Only 85% efficient. " +
      "Disabled for now."
  },
  {
    value: STOP_ORDER_MODES.PREDEFINED_VRP,
    label: "Use VRP routing",
    disabled: true,
    info: "Uses Flexio Routing Engine VRP. Experimental / non-functional. Disabled for now."
  }
];
